import React, { useState } from "react";
import { Pencil, Trash2, Check, X, Image as ImageIcon, Loader2, RefreshCw, Flame, Upload } from "lucide-react";
import { CATEGORIES, ALLERGENS } from "../lib/api";
import api from "../lib/api";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";

export default function DishCard({ dish, onUpdated, onDeleted, availableCategories }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...dish });
  const [genLoading, setGenLoading] = useState(false);
  const [upLoading, setUpLoading] = useState(false);
  const cats = availableCategories && availableCategories.length ? availableCategories : CATEGORIES;

  const uploadImage = async (file) => {
    if (!file) return;
    setUpLoading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/dishes/${dish.id}/upload-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpdated(data); toast.success("Foto caricata");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore upload"); }
    finally { setUpLoading(false); }
  };

  const save = async () => {
    try {
      const { data } = await api.put(`/dishes/${dish.id}`, form);
      onUpdated(data);
      setEditing(false);
      toast.success("Piatto aggiornato");
    } catch {
      toast.error("Errore aggiornamento");
    }
  };

  const toggleAvailable = async () => {
    try {
      const { data } = await api.put(`/dishes/${dish.id}`, { available: !dish.available });
      onUpdated(data);
    } catch { toast.error("Errore"); }
  };

  const toggleSpecial = async () => {
    try {
      const { data } = await api.post(`/dishes/${dish.id}/toggle-special`);
      onUpdated(data);
      toast.success(data.is_daily_special ? "Piatto in evidenza" : "Rimosso dagli in evidenza");
    } catch { toast.error("Errore"); }
  };
  const toggleDailyMenu = async () => {
    try {
      const { data } = await api.post(`/dishes/${dish.id}/toggle-daily-menu`);
      onUpdated(data);
      toast.success(data.is_daily_menu ? "Aggiunto al Menu del Giorno 🔥" : "Rimosso dal Menu del Giorno");
    } catch { toast.error("Errore"); }
  };

  const del = async () => {
    if (!window.confirm(`Eliminare "${dish.name}"?`)) return;
    try {
      await api.delete(`/dishes/${dish.id}`);
      onDeleted(dish.id);
      toast.success("Piatto eliminato");
    } catch { toast.error("Errore eliminazione"); }
  };

  const generateImage = async (regenerate = false) => {
    setGenLoading(true);
    if (regenerate) onUpdated({ ...dish, _generating: true, image_url: null });
    try {
      const variantSeed = Math.floor(Math.random() * 9999);
      const promptExtra = regenerate ? ` Variante creativa #${variantSeed}, angolazione diversa, composizione alternativa.` : "";
      const { data } = await api.post("/image/generate", {
        prompt: `${dish.name}. ${dish.description || ""} Ingredienti: ${(dish.ingredients||[]).join(", ")}.${promptExtra}`,
        dish_id: dish.id,
      });
      onUpdated({ ...dish, image_url: data.image_url, _generating: false });
      toast.success(regenerate ? "Nuova variante generata" : "Immagine generata");
    } catch (e) {
      onUpdated({ ...dish, _generating: false });
      toast.error(e?.response?.data?.detail || "Errore generazione immagine");
    } finally {
      setGenLoading(false);
    }
  };

  const toggleAllergen = (a) => {
    setForm((f) => ({
      ...f,
      allergens: (f.allergens || []).includes(a) ? f.allergens.filter((x) => x !== a) : [...(f.allergens || []), a],
    }));
  };

  return (
    <div data-testid="dish-card-item" className={`bg-white rounded-2xl border overflow-hidden shadow-sm hover:shadow-md transition-shadow ${dish.is_daily_special ? "border-[#D99B26] ring-2 ring-[#D99B26]/30" : "border-[#E8E2D5]"} ${!dish.available && "opacity-60"}`}>
      <div className="relative h-40 bg-[#F0EBE1]">
        {dish.image_url ? (
          <>
            <img src={dish.image_url} alt={dish.name} className="w-full h-full object-cover"/>
            <div className="absolute bottom-2 right-2 flex gap-1.5">
              <label data-testid={`upload-image-btn-${dish.id}`} className="w-9 h-9 rounded-full bg-white/95 hover:bg-white text-[#3B503A] grid place-items-center shadow-lg backdrop-blur border border-white/60 cursor-pointer" title="Carica foto dal cellulare">
                {upLoading ? <Loader2 className="animate-spin" size={16}/> : <Upload size={16}/>}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])}/>
              </label>
              <button
                data-testid={`regen-image-btn-${dish.id}`}
                onClick={() => generateImage(true)}
                disabled={genLoading}
                title="Rigenera foto AI"
                className="w-9 h-9 rounded-full bg-white/95 hover:bg-white text-[#C85A32] grid place-items-center shadow-lg backdrop-blur border border-white/60 disabled:opacity-70"
              >
                {genLoading ? <Loader2 className="animate-spin" size={16}/> : <RefreshCw size={16}/>}
              </button>
            </div>
          </>
        ) : dish._generating ? (
          <div className="w-full h-full grid place-items-center text-[#C85A32]">
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="animate-spin" size={28}/>
              <div className="text-xs font-semibold uppercase tracking-wider">Foto AI in generazione...</div>
            </div>
          </div>
        ) : (
          <div className="w-full h-full grid place-items-center text-[#6B625B]">
            <div className="flex flex-col items-center gap-2">
              <button data-testid={`gen-image-btn-${dish.id}`} onClick={() => generateImage(false)} disabled={genLoading} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 border border-[#E8E2D5] text-sm font-semibold hover:bg-white">
                {genLoading ? <Loader2 className="animate-spin" size={16}/> : <ImageIcon size={16}/>}
                {genLoading ? "Generazione..." : "Genera immagine AI"}
              </button>
              <label data-testid={`upload-image-empty-${dish.id}`} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/60 border border-[#E8E2D5] text-xs font-semibold cursor-pointer hover:bg-white">
                {upLoading ? <Loader2 className="animate-spin" size={12}/> : <Upload size={12}/>}
                {upLoading ? "Caricamento..." : "oppure carica dal cellulare"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])}/>
              </label>
            </div>
          </div>
        )}
        <div className="absolute top-2 left-2 px-2.5 py-1 rounded-full bg-[#231F1D]/85 text-white text-xs font-semibold">
          {dish.category}
        </div>
        <div className="absolute top-2 right-2 flex items-center gap-1 bg-white/95 rounded-full px-2 py-1 text-xs">
          <Switch data-testid={`toggle-available-${dish.id}`} checked={dish.available} onCheckedChange={toggleAvailable} className="scale-75"/>
        </div>
      </div>

      <div className="p-4">
        {editing ? (
          <div className="space-y-2">
            <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Nome"/>
            <div className="grid grid-cols-2 gap-2">
              <Input type="number" step="0.5" value={form.price} onChange={(e) => setForm({...form, price: parseFloat(e.target.value) || 0})} placeholder="Prezzo"/>
              <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Input value={form.description || ""} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Descrizione"/>
            <div className="flex flex-wrap gap-1">
              {ALLERGENS.map((a) => (
                <button key={a} onClick={() => toggleAllergen(a)} className={`text-[10px] px-2 py-0.5 rounded-full border ${
                  (form.allergens || []).includes(a) ? "bg-[#FEF3D6] text-[#8C580C] border-[#F4E3B8]" : "bg-white text-[#6B625B] border-[#E8E2D5]"
                }`}>{a}</button>
              ))}
            </div>
            <div className="flex gap-2 pt-2">
              <button data-testid={`save-edit-${dish.id}`} onClick={save} className="flex-1 bg-[#3B503A] text-white rounded-full py-2 text-sm font-semibold inline-flex items-center justify-center gap-1"><Check size={14}/> Salva</button>
              <button onClick={() => { setEditing(false); setForm({...dish}); }} className="flex-1 bg-[#F0EBE1] rounded-full py-2 text-sm font-semibold inline-flex items-center justify-center gap-1"><X size={14}/> Annulla</button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-2">
              <h4 className="font-serif-display text-xl font-bold text-[#231F1D]">{dish.name}</h4>
              <div className="font-heading font-bold text-[#C85A32] text-lg whitespace-nowrap">€ {Number(dish.price).toFixed(2)}</div>
            </div>
            {dish.description && <p className="mt-1 text-sm text-[#6B625B] leading-relaxed line-clamp-2">{dish.description}</p>}
            {(dish.allergens || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {dish.allergens.map((a) => (
                  <span key={a} className="text-[10px] px-2 py-0.5 rounded-full bg-[#FEF3D6] text-[#8C580C] font-semibold">{a}</span>
                ))}
              </div>
            )}
            <div className="mt-3 flex gap-2 flex-wrap">
              <button data-testid={`toggle-special-${dish.id}`} onClick={toggleSpecial} title="Prodotto in evidenza" className={`px-2.5 py-1.5 rounded-full text-xs font-bold border inline-flex items-center gap-1 ${dish.is_daily_special ? "bg-gradient-to-r from-[#D99B26] to-[#C85A32] text-white border-transparent" : "border-[#E8E2D5] text-[#8C580C] hover:bg-[#FEF3D6]"}`}>
                <Flame size={12}/>{dish.is_daily_special ? "In evidenza" : ""}
              </button>
              <button data-testid={`toggle-daily-menu-${dish.id}`} onClick={toggleDailyMenu} title="Menu del Giorno" className={`px-2.5 py-1.5 rounded-full text-xs font-bold border inline-flex items-center gap-1 ${dish.is_daily_menu ? "bg-[#3B503A] text-white border-transparent" : "border-[#E8E2D5] text-[#3B503A] hover:bg-[#EAF0E9]"}`}>
                {dish.is_daily_menu ? "🍽 Del giorno" : "🍽"}
              </button>
              <button data-testid={`edit-dish-btn-${dish.id}`} onClick={() => setEditing(true)} className="flex-1 border border-[#E8E2D5] rounded-full py-1.5 text-xs font-semibold text-[#231F1D] hover:bg-[#F0EBE1] inline-flex items-center justify-center gap-1">
                <Pencil size={12}/> Modifica
              </button>
              <button data-testid={`delete-dish-btn-${dish.id}`} onClick={del} className="border border-[#F4C7B8] text-[#C85A32] rounded-full px-3 py-1.5 text-xs font-semibold hover:bg-[#FDECE4] inline-flex items-center gap-1">
                <Trash2 size={12}/>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
